/**
 * FERP Portal - Floating Executive Assistant (DRR-131)
 *
 * Draggable, minimizable chat widget. Floats above content, can be repositioned
 * anywhere on screen. Minimizes to a compact pill. Feels alive with typing
 * indicator and smooth transitions.
 *
 * PHI-free operational assistant: answers questions about tenant workflows,
 * task status, compliance deadlines, and policy violations.
 *
 * DRY: This floating widget pattern is reusable for any conversational UI.
 * Key pattern: useRef for drag state + CSS transform for position + Portal
 * rendering to escape parent overflow/z-index stacking.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Dumb Frontend: assistant determines the response content
 * - Backend scope: assistant:query
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Paper,
  Stack,
  Group,
  Text,
  Textarea,
  ActionIcon,
  ScrollArea,
  Badge,
  Loader,
  Transition,
  Box,
} from '@mantine/core';
import {
  IconCamera,
  IconMessageChatbot,
  IconSend,
  IconAlertTriangle,
  IconChevronDown,
  IconGripHorizontal,
  IconShieldCheck,
  IconX,
  IconPlus,
} from '@tabler/icons-react';
import { Tooltip } from '@mantine/core';
import { useMutation, useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useViewLayer } from '@/auth/AuthContext';

const AGENT_HEALTH_QUERY = gql`
  query AgentServiceHealthCheck {
    agentServiceHealth {
      status
      anthropicConfigured
      serviceJwtConfigured
      activeProvider
      providerRouting
    }
  }
`;

const ASSISTANT_QUERY = gql`
  mutation AssistantQuery($input: AssistantQueryInput!) {
    assistantQuery(input: $input) {
      response
      escalatedToSupervisor
      interactionId
    }
  }
`;

interface AssistantResponse {
  assistantQuery: {
    response: string;
    escalatedToSupervisor: boolean;
    interactionId: string;
  };
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  interactionId?: string;
  escalated?: boolean;
}

export interface AssistantChatPanelProps {
  readonly opened: boolean;
  readonly onClose: () => void;
  /** Opens the AgentAssistPanel for document intake (photo/OCR flow) */
  readonly onOpenIntake?: () => void;
  /** Opens the Quick-Add Task modal */
  readonly onOpenQuickAdd?: () => void;
}

// Typing dots animation via CSS keyframes
const TYPING_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  gap: 3,
  alignItems: 'center',
  height: 20,
};

function TypingDots() {
  return (
    <span style={TYPING_STYLE}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: 'var(--mantine-color-grape-4)',
            animation: `ferp-typing 1.2s ease-in-out ${i * 0.2}s infinite`,
          }}
        />
      ))}
      <style>{`
        @keyframes ferp-typing {
          0%, 60%, 100% { opacity: 0.3; transform: scale(0.8); }
          30% { opacity: 1; transform: scale(1.1); }
        }
      `}</style>
    </span>
  );
}

export function AssistantChatPanel({ opened, onClose, onOpenIntake, onOpenQuickAdd }: AssistantChatPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [minimized, setMinimized] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const viewLayer = useViewLayer();

  // Drag state
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [askAssistant, { loading }] = useMutation<AssistantResponse>(ASSISTANT_QUERY);
  // Health check requires agent:compose scope — only architects have this
  // Skip when panel is closed to avoid unnecessary polling
  const { data: healthData } = useQuery<{
    agentServiceHealth: {
      status: string;
      anthropicConfigured: boolean;
      serviceJwtConfigured: boolean;
      activeProvider: string | null;
      providerRouting: string | null;
    };
  }>(AGENT_HEALTH_QUERY, { pollInterval: 30000, skip: !opened || viewLayer !== 'architect' });

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current && !minimized) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, minimized]);

  // Reset position when opened
  useEffect(() => {
    if (opened) {
      setPos({ x: 0, y: 0 });
      setMinimized(false);
    }
  }, [opened]);

  const handleSend = useCallback(async () => {
    const question = input.trim();
    if (!question || loading) return;

    setInput('');
    if (minimized) setMinimized(false);

    setMessages((prev) => [...prev, { role: 'user', content: question, timestamp: new Date() }]);

    try {
      const { data } = await askAssistant({
        variables: { input: { question, scopes: ['TASKS', 'COMPLIANCE', 'DEADLINES'] } },
      });
      if (data?.assistantQuery) {
        setMessages((prev) => [...prev, {
          role: 'assistant',
          content: data.assistantQuery.response,
          timestamp: new Date(),
          interactionId: data.assistantQuery.interactionId,
          escalated: data.assistantQuery.escalatedToSupervisor,
        }]);
      }
    } catch (err) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: `Error: ${err instanceof Error ? err.message : 'Failed to reach assistant'}`,
        timestamp: new Date(),
      }]);
    }
  }, [input, loading, askAssistant, minimized]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // Drag handlers
  const onDragStart = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
    const onMove = (ev: PointerEvent) => {
      if (!dragRef.current) return;
      setPos({
        x: dragRef.current.origX + (ev.clientX - dragRef.current.startX),
        y: dragRef.current.origY + (ev.clientY - dragRef.current.startY),
      });
    };
    const onUp = () => {
      dragRef.current = null;
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [pos]);

  if (!opened) return null;

  return (
    <Box
      ref={containerRef}
      style={{
        position: 'fixed',
        bottom: `calc(24px - ${pos.y}px)`,
        right: `calc(24px - ${pos.x}px)`,
        zIndex: 1000,
        transition: dragRef.current ? 'none' : 'width 0.2s ease, height 0.2s ease',
      }}
    >
      <Transition mounted={!minimized} transition="slide-up" duration={200}>
        {(styles) => (
          <Paper
            shadow="xl"
            radius="lg"
            withBorder
            style={{
              ...styles,
              width: 380,
              height: 480,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Header — draggable */}
            <Group
              justify="space-between"
              px="sm"
              py={8}
              style={{
                cursor: 'grab',
                borderBottom: '1px solid var(--mantine-color-default-border)',
                background: 'var(--mantine-color-grape-0)',
                userSelect: 'none',
                flexShrink: 0,
              }}
              onPointerDown={onDragStart}
            >
              <Group gap={6}>
                <IconGripHorizontal size={14} style={{ opacity: 0.4 }} />
                <IconMessageChatbot size={16} color="var(--mantine-color-grape-6)" />
                <Text size="sm" fw={600} c="grape.8">Assistant</Text>
                <Tooltip
                  label={healthData?.agentServiceHealth?.providerRouting ?? 'LLM provider info unavailable'}
                  position="bottom"
                  withArrow
                >
                  <Badge
                    variant="light"
                    color={healthData?.agentServiceHealth?.status === 'healthy' ? 'green' : 'yellow'}
                    size="xs"
                    style={{ cursor: 'help' }}
                  >
                    <Group gap={2}>
                      <IconShieldCheck size={8} />
                      {healthData?.agentServiceHealth?.status === 'healthy'
                        ? healthData?.agentServiceHealth?.activeProvider ?? 'Online'
                        : 'Offline'}
                    </Group>
                  </Badge>
                </Tooltip>
              </Group>
              <Group gap={4}>
                {onOpenQuickAdd && (
                  <Tooltip label="Quick Add Task" position="bottom">
                    <ActionIcon size="xs" variant="subtle" color="teal" onClick={onOpenQuickAdd} aria-label="Quick Add Task">
                      <IconPlus size={14} />
                    </ActionIcon>
                  </Tooltip>
                )}
                <ActionIcon size="xs" variant="subtle" onClick={() => setMinimized(true)} aria-label="Minimize">
                  <IconChevronDown size={14} />
                </ActionIcon>
                <ActionIcon size="xs" variant="subtle" onClick={onClose} aria-label="Close">
                  <IconX size={14} />
                </ActionIcon>
              </Group>
            </Group>

            {/* Messages */}
            <ScrollArea flex={1} px="sm" pt="xs" viewportRef={scrollRef}>
              <Stack gap={8}>
                {messages.length === 0 && (
                  <Text size="xs" c="dimmed" ta="center" py="lg">
                    Ask about tasks, compliance, workflows, or operations.
                  </Text>
                )}
                {messages.map((msg, i) => (
                  <Box
                    key={i}
                    style={{
                      maxWidth: '85%',
                      alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <Paper
                      px="sm"
                      py={6}
                      radius="lg"
                      style={{
                        background: msg.role === 'user'
                          ? 'var(--mantine-color-blue-6)'
                          : 'var(--mantine-color-gray-1)',
                        borderBottomRightRadius: msg.role === 'user' ? 4 : undefined,
                        borderBottomLeftRadius: msg.role === 'assistant' ? 4 : undefined,
                      }}
                    >
                      <Text
                        size="sm"
                        c={msg.role === 'user' ? 'white' : undefined}
                        style={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}
                      >
                        {msg.content}
                      </Text>
                    </Paper>
                    <Group gap={4} mt={2} justify={msg.role === 'user' ? 'flex-end' : 'flex-start'}>
                      <Text size="xs" c="dimmed">
                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Text>
                      {msg.escalated && (
                        <Badge variant="light" color="orange" size="xs">
                          <IconAlertTriangle size={8} /> escalated
                        </Badge>
                      )}
                    </Group>
                  </Box>
                ))}
                {loading && (
                  <Box style={{ alignSelf: 'flex-start' }}>
                    <Paper px="sm" py={8} radius="lg" bg="gray.1">
                      <TypingDots />
                    </Paper>
                  </Box>
                )}
              </Stack>
            </ScrollArea>

            {/* Input */}
            <Group gap={6} px="sm" py={8} style={{ borderTop: '1px solid var(--mantine-color-default-border)', flexShrink: 0 }}>
              {onOpenIntake && (
                <ActionIcon
                  variant="light"
                  color="violet"
                  size="lg"
                  radius="xl"
                  onClick={onOpenIntake}
                  aria-label="Scan document"
                >
                  <IconCamera size={16} />
                </ActionIcon>
              )}
              <Textarea
                flex={1}
                placeholder="Ask anything..."
                value={input}
                onChange={(e) => setInput(e.currentTarget.value)}
                onKeyDown={handleKeyDown}
                minRows={1}
                maxRows={3}
                autosize
                disabled={loading}
                radius="xl"
                size="sm"
                styles={{ input: { paddingRight: 8 } }}
              />
              <ActionIcon
                variant="filled"
                color="grape"
                size="lg"
                radius="xl"
                onClick={handleSend}
                disabled={!input.trim() || loading}
                aria-label="Send"
              >
                <IconSend size={16} />
              </ActionIcon>
            </Group>
          </Paper>
        )}
      </Transition>

      {/* Minimized pill */}
      <Transition mounted={minimized} transition="pop" duration={150}>
        {(styles) => (
          <Paper
            shadow="lg"
            radius="xl"
            withBorder
            px="md"
            py={8}
            style={{
              ...styles,
              cursor: 'pointer',
              background: 'var(--mantine-color-grape-6)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              userSelect: 'none',
            }}
            onPointerDown={onDragStart}
            onClick={() => setMinimized(false)}
          >
            <IconMessageChatbot size={18} color="white" />
            <Text size="sm" fw={600} c="white">
              Assistant
              {messages.length > 0 && ` (${messages.length})`}
            </Text>
            {loading && <Loader size={12} color="white" />}
          </Paper>
        )}
      </Transition>
    </Box>
  );
}

export default AssistantChatPanel;
